import { useEffect, useRef, useState } from 'react';

export interface BillingAddressData {
    name: string; // Auto-populated from customer info
    phone?: string; // Auto-populated from customer info
    email?: string; // Auto-populated from customer info
    address: string;
    address2?: string;
    city: string;
    region: string; // 2-char state code (e.g., "CA", "NY")
    postal: string;
    country: string; // 2-char country code (e.g., "US")
}

interface BillingAddressProps {
    onAddressChange: (address: BillingAddressData) => void;
    initialAddress?: Partial<BillingAddressData>;
    customerName?: string; // Auto-populate from customer info
    customerPhone?: string; // Auto-populate from customer info
    customerEmail?: string; // Auto-populate from customer info
}

// US state codes for validation
const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

export default function BillingAddress({
    onAddressChange,
    initialAddress,
    customerName,
    customerPhone,
    customerEmail
}: BillingAddressProps) {
    const [address, setAddress] = useState<BillingAddressData>(() => ({
        name: initialAddress?.name || customerName || '',
        phone: initialAddress?.phone || customerPhone || '',
        email: initialAddress?.email || customerEmail || '',
        address: initialAddress?.address || '',
        address2: initialAddress?.address2 || '',
        city: initialAddress?.city || '',
        region: initialAddress?.region || '',
        postal: initialAddress?.postal || '',
        country: initialAddress?.country || 'US',
    }));

    // Use ref to avoid stale closure issues with callback
    const onAddressChangeRef = useRef(onAddressChange);
    useEffect(() => {
        onAddressChangeRef.current = onAddressChange;
    }, [onAddressChange]);

    // Notify parent when address changes
    useEffect(() => {
        onAddressChangeRef.current(address);
    }, [address]);

    const updateAddress = (field: keyof BillingAddressData, value: string) => {
        const updated = { ...address, [field]: value };
        setAddress(updated);
        // Note: onAddressChange is called automatically via useEffect when address changes
    };

    return (
        <div className="mb-8">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900">Billing Address</h2>
            </div>

            <div className="space-y-4">
                {/* Street Address */}
                <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="address"
                        value={address.address}
                        onChange={(e) => updateAddress('address', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                        placeholder="123 Main St"
                        maxLength={30}
                        required
                    />
                </div>

                {/* Address Line 2 (Optional) */}
                <div>
                    <label htmlFor="address2" className="block text-sm font-medium text-gray-700 mb-1">
                        Apartment, suite, etc. <span className="text-gray-500 text-xs">(optional)</span>
                    </label>
                    <input
                        type="text"
                        id="address2"
                        value={address.address2}
                        onChange={(e) => updateAddress('address2', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                        placeholder="Apt 4B"
                        maxLength={30}
                    />
                </div>

                {/* City, State, ZIP - 3 column grid */}
                <div className="grid grid-cols-6 gap-4">
                    {/* City */}
                    <div className="col-span-6 sm:col-span-3">
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                            City <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="city"
                            value={address.city}
                            onChange={(e) => updateAddress('city', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                            placeholder="San Francisco"
                            maxLength={30}
                            required
                        />
                    </div>

                    {/* State */}
                    <div className="col-span-3 sm:col-span-1">
                        <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">
                            State <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="region"
                            value={address.region}
                            onChange={(e) => updateAddress('region', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow appearance-none bg-white"
                            required
                        >
                            <option value="">--</option>
                            {US_STATES.map((state) => (
                                <option key={state} value={state}>
                                    {state}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* ZIP Code */}
                    <div className="col-span-3 sm:col-span-2">
                        <label htmlFor="postal" className="block text-sm font-medium text-gray-700 mb-1">
                            ZIP Code <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="postal"
                            value={address.postal}
                            onChange={(e) => {
                                // Only allow digits and hyphen for US ZIP codes
                                const value = e.target.value.replace(/[^\d-]/g, '');
                                if (value.length <= 10) {
                                    updateAddress('postal', value);
                                }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-shadow"
                            placeholder="94102"
                            pattern="^\d{5}(-?\d{4})?$"
                            maxLength={10}
                            required
                        />
                    </div>
                </div>

                {/* AVS Info Banner */}
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                    <svg className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-xs text-blue-800">
                        <p className="font-medium">Why do we need your address?</p>
                        <p className="mt-1 text-blue-700">
                            We use your billing address to verify your identity and prevent fraud. This helps protect both you and the restaurant.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
